// File: src/pages/DashboardPage.jsx
// Description: Modern, professional dashboard with enhanced visual appeal and user experience.

import React, { useContext, useRef } from 'react';
import { AuthContext } from '../context/AuthContext';
import {
  Container,
  Typography,
  Grid,
  Card,
  CardContent,
  Box,
  Button,
  IconButton,
  Avatar,
  Tooltip,
  Divider,
  Chip,
  Stack,
  useTheme,
  useMediaQuery
} from '@mui/material';
import MuiLink from '@mui/material/Link';
import { Link, useNavigate } from 'react-router-dom';
import QRCode from 'react-qr-code';
import html2canvas from 'html2canvas';

// Material UI Icons for Contact Information
import PhoneIcon from '@mui/icons-material/Phone';
import FileCopyIcon from '@mui/icons-material/FileCopy';
import EmailIcon from '@mui/icons-material/Email';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import DownloadIcon from '@mui/icons-material/Download';
import ShareIcon from '@mui/icons-material/Share';
import EditIcon from '@mui/icons-material/Edit';
import QrCodeIcon from '@mui/icons-material/QrCode';
import VisibilityIcon from '@mui/icons-material/Visibility';
import Build from '@mui/icons-material/Build';

// FontAwesome icons from react-icons/fa
import {
  FaLinkedin,
  FaTwitter,
  FaFacebook,
  FaInstagram,
  FaYoutube,
  FaGithub,
  FaBlog,
  FaDownload,
  FaFilePdf,
  FaAddressCard,
  FaShareAlt,
  FaAngellist,
  FaUserTie,
  FaHandshake,
  FaDeviantart,
  FaFlickr,
  Fa500Px,
  FaRegSmile,
  FaImage,
  FaVimeoV,
  FaTwitch,
  FaVideo,
  FaReddit,
  FaTumblr,
  FaCommentAlt,
  FaMicrophone,
  FaUsers,
  FaDiscord,
  FaChair,
  FaSpotify,
  FaSoundcloud,
  FaBandcamp,
  FaPodcast,
  FaApple,
  FaMusic,
  FaStackOverflow,
  FaDev,
  FaKaggle,
  FaCodepen,
  FaBitbucket,
  FaGitlab,
  FaAmazon,
  FaEbay,
  FaEtsy,
  FaShopify,
  FaCartPlus,
  FaStore,
  FaShoppingCart,
  FaShoppingBag,
  FaMedium,
  FaWordpress,
  FaGhost,
  FaBlogger,
  FaNewspaper,
  FaEnvelopeOpenText,
  FaGraduationCap,
  FaGlobe
} from 'react-icons/fa';

// Icons from react-icons/si
import {
  SiWhatsapp,
  SiTiktok,
  SiXing,
  SiViadeo,
  SiCrunchbase,
  SiGlassdoor,
  SiPinterest,
  SiBehance,
  SiDribbble,
  SiDailymotion,
  SiMastodon,
  SiHackerrank,
  SiReplit,
  SiSourceforge,
  SiAlibabacloud,
  SiRakuten,
  SiMixcloud,
  SiAudiomack,
  SiAdobexd,
  SiBilibili,
  SiWooCommerce,
  SiResearchgate,
  SiGooglescholar
} from 'react-icons/si';

// Import AI components
import AIPersonalizationCard from '../components/AIPersonalizationCard';
import CareerInsightsCard from '../components/CareerInsightsCard';
import AINetworkingAssistantWidget from '../components/AINetworkingAssistantWidget';

// Helper function to return a captivating call-to-action text based on the platform field
const getCTA = (field) => {
  switch (field) {
    case 'facebook': return 'Visit FB';
    case 'instagram': return 'View IG';
    case 'twitter': return 'Follow on X';
    case 'linkedin': return 'Connect';
    case 'youtube': return 'Watch Videos';
    case 'whatsapp': return 'Chat Now';
    case 'tiktok': return 'See TikTok';
    case 'crunchbase': return 'Explore CB';
    case 'glassdoor': return 'Read Reviews';
    case 'researchgate': return 'Research Now';
    case 'academia': return 'Academic Profile';
    case 'google_scholar': return 'Cite Me';
    case 'pinterest': return 'See Pins';
    case 'behance': return 'View Portfolio';
    case 'dribbble': return 'Check Dribbble';
    case 'twitch': return 'Watch Twitch';
    case 'reddit': return 'Visit Reddit';
    case 'discord': return 'Join Chat';
    case 'amazon': return 'Shop on Amazon';
    case 'ebay': return 'Visit eBay';
    case 'etsy': return 'Browse Etsy';
    case 'shopify': return 'Visit Store';
    case 'medium': return 'Read Articles';
    case 'wordpress': return 'Visit Blog';
    case 'substack': return 'Subscribe';
    default: return 'Visit';
  }
};

const DashboardPage = () => {
  const { user } = useContext(AuthContext);
  const qrRef = useRef(null);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const navigate = useNavigate();

  // Construct public profile URL based on user's email
  const publicUrl = user?.email
    ? `${window.location.origin}/u/${user.email.replace('@', '-').replace(/\./g, '-')}`
    : '';

  // Download QR Code as PNG or JPEG
  const handleDownload = async (format) => {
    if (!qrRef.current) return;
    try {
      const canvas = await html2canvas(qrRef.current, { backgroundColor: null });
      const link = document.createElement('a');
      if (format === 'png') {
        link.download = 'qr_code.png';
        link.href = canvas.toDataURL('image/png');
      } else if (format === 'jpeg') {
        link.download = 'qr_code.jpeg';
        link.href = canvas.toDataURL('image/jpeg');
      }
      link.click();
    } catch (error) {
      console.error('Error downloading QR code', error);
    }
  };

  // Download QR Code as SVG (basic implementation)
  const handleSvgDownload = () => {
    alert('SVG download functionality to be implemented.');
  };

  // Generate and download a vCard file
  const handleVcardDownload = () => {
    const vCardData = `
BEGIN:VCARD
VERSION:3.0
N:${user?.name}
FN:${user?.name}
EMAIL:${user?.email}
TEL:${user?.phone_number || ''}
END:VCARD
    `;
    const blob = new Blob([vCardData], { type: 'text/vcard;charset=utf-8' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = 'contact.vcf';
    link.click();
  };

  // Share profile using Web Share API if available
  const handleShareProfile = () => {
    if (navigator.share) {
      navigator.share({
        title: 'My Public Profile',
        text: 'Check out my public profile!',
        url: publicUrl,
      });
    } else {
      alert('Share functionality is not supported in this browser.');
    }
  };

  // Print the page as PDF
  const handlePrintAsPDF = () => {
    window.print();
  };

  // Updated social media categories (ensuring keys match those returned from the backend)
  const socialMediaCategories = [
    {
      category: 'Social Media & Communication',
      platforms: [
        { field: 'facebook', label: 'Facebook Page', icon: <FaFacebook size={20} color="#4267B2" /> },
        { field: 'instagram', label: 'Instagram Profile', icon: <FaInstagram size={20} color="#C13584" /> },
        { field: 'twitter', label: 'Twitter (X) Profile', icon: <FaTwitter size={20} color="#1DA1F2" /> },
        { field: 'linkedin', label: 'LinkedIn Profile', icon: <FaLinkedin size={20} color="#0e76a8" /> },
        { field: 'youtube', label: 'YouTube Channel', icon: <FaYoutube size={20} color="#FF0000" /> },
        { field: 'whatsapp', label: 'WhatsApp Contact Link', icon: <SiWhatsapp size={20} color="#25D366" /> },
        { field: 'tiktok', label: 'TikTok Profile', icon: <SiTiktok size={20} color="#010101" /> }
      ]
    },
    {
      category: 'Professional, Business & Academic Networks',
      platforms: [
        { field: 'crunchbase', label: 'Crunchbase Profile', icon: <SiCrunchbase size={20} color="#003366" /> },
        { field: 'glassdoor', label: 'Glassdoor Profile', icon: <SiGlassdoor size={20} color="#0B84A5" /> },
        { field: 'researchgate', label: 'ResearchGate', icon: <SiResearchgate size={20} color="#009999" /> },
        { field: 'academia', label: 'Academia.edu Profile', icon: <FaGraduationCap size={20} color="#6A1B9A" /> },
        { field: 'google_scholar', label: 'Google Scholar', icon: <SiGooglescholar size={20} color="#4285F4" /> }
      ]
    },
    {
      category: 'Creative & Visual Platforms',
      platforms: [
        { field: 'pinterest', label: 'Pinterest Profile', icon: <SiPinterest size={20} color="#E60023" /> },
        { field: 'behance', label: 'Behance Profile', icon: <SiBehance size={20} color="#1769FF" /> },
        { field: 'dribbble', label: 'Dribbble Profile', icon: <SiDribbble size={20} color="#EA4C89" /> }
      ]
    },
    {
      category: 'Video & Live Streaming Platforms',
      platforms: [
        { field: 'twitch', label: 'Twitch Channel', icon: <FaTwitch size={20} color="#6441A5" /> }
      ]
    },
    {
      category: 'Community & Forums',
      platforms: [
        { field: 'reddit', label: 'Reddit Profile', icon: <FaReddit size={20} color="#FF4500" /> },
        { field: 'discord', label: 'Discord', icon: <FaDiscord size={20} color="#7289da" /> },
        { field: 'tumblr', label: 'Tumblr Profile', icon: <FaTumblr size={20} color="#34526f" /> }
      ]
    },
    {
      category: 'Tech & Developer Communities',
      platforms: [
        { field: 'github', label: 'GitHub Profile', icon: <FaGithub size={20} color="#333" /> },
        { field: 'stack_overflow', label: 'Stack Overflow Profile', icon: <FaStackOverflow size={20} color="#F48024" /> }
      ]
    },
    {
      category: 'E-commerce & Marketplace Platforms',
      platforms: [
        { field: 'amazon', label: 'Amazon Profile', icon: <FaAmazon size={20} color="#FF9900" /> },
        { field: 'ebay', label: 'eBay Profile', icon: <FaEbay size={20} color="#E53238" /> },
        { field: 'etsy', label: 'Etsy Profile', icon: <FaEtsy size={20} color="#D5641C" /> },
        { field: 'shopify', label: 'Shopify Store', icon: <FaShopify size={20} color="#96BF48" /> }
      ]
    },
    {
      category: 'Blogging & Publishing Platforms',
      platforms: [
        { field: 'medium', label: 'Medium Profile', icon: <FaMedium size={20} color="#000" /> },
        { field: 'wordpress', label: 'WordPress Blog', icon: <FaWordpress size={20} color="#21759B" /> },
        { field: 'substack', label: 'Substack Newsletter', icon: <FaEnvelopeOpenText size={20} color="#000" /> }
      ]
    }
  ];

  return (
    <Container maxWidth="xl" sx={{ mt: 4, mb: 4 }}>
      {/* Welcome Header */}
      <Box sx={{ textAlign: 'center', mb: 6 }}>
        <Typography 
          variant="h3" 
          gutterBottom 
          sx={{ 
            fontWeight: 800,
            background: 'linear-gradient(135deg, #1e40af 0%, #3b82f6 100%)',
            backgroundClip: 'text',
            WebkitBackgroundClip: 'text',
            WebkitTextFillColor: 'transparent',
            mb: 2
          }}
        >
          Welcome back, {user?.name || 'User'}! 👋
        </Typography>
        <Typography variant="h6" color="text.secondary" sx={{ maxWidth: '700px', mx: 'auto' }}>
          Manage your digital business card and connect with your network
        </Typography>

        {/* Mentor Park CTA */}
        <Box sx={{ mt: 3 }}>
          <Button
            variant="contained"
            size="large"
            startIcon={<Build />}
            onClick={() => navigate('/mentor-park/tools')}
            sx={{ borderRadius: 2, px: 3, py: 1.25 }}
          >
            Go to Mentor Park — Start with Career Tools & Preparation
          </Button>
        </Box>
      </Box>

      <Grid container spacing={4}>
        {/* LEFT COLUMN: Profile & Additional Info */}
        <Grid item xs={12} lg={8}>
          {/* Profile Card */}
          <Card sx={{ 
            borderRadius: 3, 
            boxShadow: '0 10px 30px rgba(0, 0, 0, 0.1)',
            mb: 4,
            background: 'linear-gradient(135deg, #ffffff 0%, #f8fafc 100%)',
            border: `1px solid ${theme.palette.divider}`
          }}>
            <CardContent sx={{ p: 4 }}>
              <Grid container spacing={3} alignItems="center">
                <Grid item xs={12} sm={3} textAlign="center">
                  <Avatar
                    src={user?.profile_picture}
                    alt={user?.name}
                    sx={{ 
                      width: 120, 
                      height: 120, 
                      margin: '0 auto',
                      border: `4px solid ${theme.palette.primary[100]}`,
                      boxShadow: '0 8px 25px rgba(0, 0, 0, 0.15)'
                    }}
                  />
                </Grid>
                <Grid item xs={12} sm={9}>
                  <Typography variant="h4" sx={{ fontWeight: 700, mb: 1 }}>
                    {user?.name || 'Your Name'}
                  </Typography>
                  <Typography variant="h6" color="primary" sx={{ fontWeight: 600, mb: 2 }}>
                    {user?.job_title || 'Job Title'} {user?.company && `@ ${user.company}`}
                  </Typography>
                  {user?.location && (
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                      <LocationOnIcon sx={{ mr: 1, color: theme.palette.text.secondary }} />
                      <Typography variant="body1" color="text.secondary">
                      {user.location}
                    </Typography>
                    </Box>
                  )}
                  {/* Action Buttons */}
                  <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', justifyContent: 'center' }}>
                    <Button
                      variant="outlined"
                      size="large"
                      startIcon={<EditIcon />}
                      onClick={() => navigate('/profile/edit')}
                      sx={{ 
                        borderRadius: 2,
                        px: 4,
                        py: 1.5,
                        borderWidth: 2,
                        '&:hover': { borderWidth: 2 }
                      }}
                    >
                      Edit Profile
                    </Button>
                    
                    <Button
                      variant="gradient"
                      size="large"
                      startIcon={<VisibilityIcon />}
                      onClick={() => {
                        if (user?.public_slug) {
                          const profileUrl = `${window.location.origin}/u/${user.public_slug}`;
                          window.open(profileUrl, '_blank');
                        }
                      }}
                      sx={{ 
                        borderRadius: 2,
                        px: 4,
                        py: 1.5,
                        fontSize: '1rem',
                        fontWeight: 600
                      }}
                    >
                      View My Profile
                    </Button>
                    
                    <Button
                      variant="outlined"
                      size="large"
                      startIcon={<QrCodeIcon />}
                      onClick={() => navigate('/profile/qr')}
                      sx={{ 
                        borderRadius: 2,
                        px: 4,
                        py: 1.5,
                        borderWidth: 2,
                        '&:hover': { borderWidth: 2 }
                      }}
                    >
                      View QR Code
                    </Button>
                  </Box>
                </Grid>
              </Grid>
              
              {user?.bio && (
                <Box sx={{ mt: 3, p: 3, backgroundColor: theme.palette.background.default, borderRadius: 2 }}>
                  <Typography variant="body1" sx={{ lineHeight: 1.7 }}>
                    {user.bio}
                </Typography>
                </Box>
              )}
              
                {user?.interests && (
                <Box sx={{ mt: 3 }}>
                  <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 2 }}>
                    Interests & Skills
                  </Typography>
                  <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                    {user.interests.split(',').map((interest, index) => (
                      <Chip
                        key={index}
                        label={interest.trim()}
                        size="small"
                        sx={{ 
                          backgroundColor: theme.palette.primary[100],
                          color: theme.palette.primary[700],
                          fontWeight: 500
                        }}
                      />
                    ))}
                  </Box>
                </Box>
              )}
              {/* Added hint for Mentor Park priority */}
              <Box sx={{ mt: 3, p: 2, borderRadius: 2, border: `1px dashed ${theme.palette.primary.main}` }}>
                <Typography variant="body2" color="text.secondary">
                  Tip: Explore <strong>Mentor Park</strong> — start with <strong>Career Tools & Preparation</strong> to tailor your applications.
                </Typography>
              </Box>
            </CardContent>
          </Card>

          {/* Contact Information Card */}
          <Card sx={{ 
            borderRadius: 3, 
            boxShadow: '0 10px 30px rgba(0, 0, 0, 0.1)',
            mb: 4,
            background: 'linear-gradient(135deg, #ffffff 0%, #f8fafc 100%)',
            border: `1px solid ${theme.palette.divider}`
          }}>
            <CardContent sx={{ p: 4 }}>
              <Typography variant="h5" gutterBottom sx={{ fontWeight: 600, mb: 3 }}>
                Contact Information
              </Typography>
              <Grid container spacing={3}>
                {user?.phone_number && (
                  <Grid item xs={12} sm={6}>
                    <Box sx={{ 
                      display: 'flex', 
                      alignItems: 'center', 
                      p: 2, 
                      backgroundColor: theme.palette.background.default,
                      borderRadius: 2,
                      border: `1px solid ${theme.palette.divider}`
                    }}>
                      <PhoneIcon sx={{ mr: 2, color: theme.palette.primary.main }} />
                      <Box>
                        <Typography variant="body2" color="text.secondary">
                          Phone
                        </Typography>
                    <MuiLink
                      href={`tel:${user.phone_number}`}
                      underline="hover"
                          sx={{ fontWeight: 600, color: theme.palette.text.primary }}
                    >
                          {user.phone_number}
                    </MuiLink>
                  </Box>
                    </Box>
                  </Grid>
                )}
                
                {user?.email && (
                  <Grid item xs={12} sm={6}>
                    <Box sx={{ 
                      display: 'flex', 
                      alignItems: 'center', 
                      p: 2, 
                      backgroundColor: theme.palette.background.default,
                      borderRadius: 2,
                      border: `1px solid ${theme.palette.divider}`
                    }}>
                      <EmailIcon sx={{ mr: 2, color: theme.palette.primary.main }} />
                      <Box>
                        <Typography variant="body2" color="text.secondary">
                          Email
                        </Typography>
                    <MuiLink
                      href={`mailto:${user.email}`}
                      underline="hover"
                          sx={{ fontWeight: 600, color: theme.palette.text.primary }}
                    >
                          {user.email}
                    </MuiLink>
                  </Box>
                    </Box>
                  </Grid>
                )}

                {user?.whatsapp && (
                  <Grid item xs={12} sm={6}>
                    <Box sx={{ 
                      display: 'flex', 
                      alignItems: 'center', 
                      p: 2, 
                      backgroundColor: theme.palette.background.default,
                      borderRadius: 2,
                      border: `1px solid ${theme.palette.divider}`
                    }}>
                      <SiWhatsapp size={24} style={{ marginRight: 16, color: '#25D366' }} />
                      <Box>
                        <Typography variant="body2" color="text.secondary">
                          WhatsApp
                        </Typography>
                    <MuiLink
                      href={`https://wa.me/${user.whatsapp}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      underline="hover"
                          sx={{ fontWeight: 600, color: theme.palette.text.primary }}
                    >
                      Chat Now
                    </MuiLink>
                  </Box>
                    </Box>
                  </Grid>
                )}

                {user?.website && (
                  <Grid item xs={12} sm={6}>
                    <Box sx={{ 
                      display: 'flex', 
                      alignItems: 'center', 
                      p: 2, 
                      backgroundColor: theme.palette.background.default,
                      borderRadius: 2,
                      border: `1px solid ${theme.palette.divider}`
                    }}>
                      <FaGlobe size={24} style={{ marginRight: 16, color: theme.palette.primary.main }} />
                      <Box>
                        <Typography variant="body2" color="text.secondary">
                          Website
                        </Typography>
                    <MuiLink
                      href={user.website}
                      target="_blank"
                      rel="noopener noreferrer"
                      underline="hover"
                          sx={{ fontWeight: 600, color: theme.palette.text.primary }}
                    >
                      Visit Site
                    </MuiLink>
                  </Box>
                  </Box>
                  </Grid>
                )}
              </Grid>
            </CardContent>
          </Card>

          {/* AI Features Section */}
          <Grid container spacing={3} sx={{ mb: 4 }}>
            <Grid item xs={12} md={6} lg={4}>
              <AIPersonalizationCard />
            </Grid>
            <Grid item xs={12} md={6} lg={4}>
              <CareerInsightsCard />
            </Grid>
            <Grid item xs={12} md={6} lg={4}>
              <AINetworkingAssistantWidget />
            </Grid>
          </Grid>

          {/* Social Media Platforms */}
          {socialMediaCategories.map((category, categoryIndex) => {
            const hasPlatforms = category.platforms.some(platform => user?.[platform.field]);
            
            if (!hasPlatforms) return null;

            return (
              <Card key={categoryIndex} sx={{ 
                borderRadius: 3, 
                boxShadow: '0 10px 30px rgba(0, 0, 0, 0.1)',
                mb: 4,
                background: 'linear-gradient(135deg, #ffffff 0%, #f8fafc 100%)',
                border: `1px solid ${theme.palette.divider}`
              }}>
                <CardContent sx={{ p: 4 }}>
                  <Typography variant="h5" gutterBottom sx={{ fontWeight: 600, mb: 3 }}>
                    {category.category}
                </Typography>
                  <Grid container spacing={2}>
                    {category.platforms.map((platform, platformIndex) => {
                      if (!user?.[platform.field]) return null;
                      
                return (
                        <Grid item xs={12} sm={6} md={4} key={platformIndex}>
                          <Box sx={{
                            display: 'flex',
                            alignItems: 'center',
                            p: 2,
                            backgroundColor: theme.palette.background.default,
                            borderRadius: 2,
                            border: `1px solid ${theme.palette.divider}`,
                            transition: 'all 0.2s ease-in-out',
                            '&:hover': {
                              backgroundColor: theme.palette.primary[50],
                              borderColor: theme.palette.primary.main,
                              transform: 'translateY(-2px)',
                              boxShadow: '0 4px 12px rgba(0, 0, 0, 0.1)'
                            }
                          }}>
                            {platform.icon}
                            <Box sx={{ ml: 2, flex: 1 }}>
                              <Typography variant="body2" sx={{ fontWeight: 500, mb: 0.5 }}>
                                {platform.label}
                    </Typography>
                              <MuiLink
                                href={user[platform.field]}
                              target="_blank"
                              rel="noopener noreferrer"
                                underline="hover"
                                sx={{ 
                                  fontSize: '0.875rem',
                                  color: theme.palette.primary.main,
                                  fontWeight: 600
                                }}
                              >
                            {getCTA(platform.field)}
                              </MuiLink>
                    </Box>
                  </Box>
                        </Grid>
                );
              })}
                  </Grid>
            </CardContent>
          </Card>
            );
          })}
        </Grid>

        {/* RIGHT COLUMN: QR Code & Actions */}
        <Grid item xs={12} lg={4}>
          {/* QR Code Card */}
          <Card sx={{ 
            borderRadius: 3, 
            boxShadow: '0 10px 30px rgba(0, 0, 0, 0.1)',
            mb: 4,
            background: 'linear-gradient(135deg, #ffffff 0%, #f8fafc 100%)',
            border: `1px solid ${theme.palette.divider}`,
            position: 'sticky',
            top: 24
          }}>
            <CardContent sx={{ p: 4, textAlign: 'center' }}>
              <Typography variant="h6" gutterBottom sx={{ fontWeight: 600, mb: 3 }}>
                Your QR Code
              </Typography>
              
              {/* QR Code Display */}
              <Box
                ref={qrRef}
                sx={{
                  p: 3, 
                  backgroundColor: 'white',
                  borderRadius: 2,
                  border: `2px solid ${theme.palette.divider}`,
                  display: 'inline-block',
                  mb: 3
                }}
              >
                <QRCode
                  value={publicUrl}
                  size={200}
                  level="H"
                  fgColor="#000000"
                  bgColor="#ffffff"
                />
              </Box>

              {/* Download Options */}
              <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                Download Options
              </Typography>
              <Stack direction="row" spacing={1} sx={{ mb: 3, justifyContent: 'center' }}>
                <Button
                  size="small"
                  variant="outlined"
                  startIcon={<DownloadIcon />}
                  onClick={() => handleDownload('png')}
                  sx={{ borderRadius: 2, borderWidth: '2px' }}
                >
                  PNG
                </Button>
                <Button
                  size="small"
                  variant="outlined"
                  startIcon={<DownloadIcon />}
                  onClick={() => handleDownload('jpeg')}
                  sx={{ borderRadius: 2, borderWidth: '2px' }}
                >
                  JPEG
                </Button>
                <Button
                  size="small"
                  variant="outlined"
                  startIcon={<DownloadIcon />}
                  onClick={handleVcardDownload}
                  sx={{ borderRadius: 2, borderWidth: '2px' }}
                >
                  vCard
                </Button>
              </Stack>

              {/* Action Buttons */}
              <Stack spacing={2}>
                <Button
                  fullWidth
                  variant="gradient"
                  startIcon={<ShareIcon />}
                  onClick={handleShareProfile}
                  sx={{ borderRadius: 2, py: 1.5 }}
                >
                  Share Profile
                </Button>
                <Button
                  fullWidth
                  variant="outlined"
                  startIcon={<FaFilePdf />}
                  onClick={handlePrintAsPDF}
                  sx={{ borderRadius: 2, borderWidth: '2px', py: 1.5 }}
                >
                  Export as PDF
                </Button>
              </Stack>
            </CardContent>
          </Card>

          {/* Quick Stats Card */}
          <Card sx={{ 
            borderRadius: 3, 
            boxShadow: '0 10px 30px rgba(0, 0, 0, 0.1)',
            background: 'linear-gradient(135deg, #ffffff 0%, #f8fafc 100%)',
            border: `1px solid ${theme.palette.divider}`
          }}>
            <CardContent sx={{ p: 4 }}>
              <Typography variant="h6" gutterBottom sx={{ fontWeight: 600, mb: 3 }}>
                Profile Stats
              </Typography>
              
              <Stack spacing={3}>
                <Box sx={{ textAlign: 'center' }}>
                  <Typography variant="h4" sx={{ fontWeight: 700, color: theme.palette.primary.main }}>
                    {socialMediaCategories.reduce((total, category) => 
                      total + category.platforms.filter(platform => user?.[platform.field]).length, 0
                    )}
                </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Connected Platforms
                </Typography>
              </Box>
                
                <Box sx={{ textAlign: 'center' }}>
                  <Typography variant="h4" sx={{ fontWeight: 700, color: theme.palette.secondary.main }}>
                    {user?.profile_views || 0}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Profile Views
                  </Typography>
                </Box>
                
                <Box sx={{ textAlign: 'center' }}>
                  <Typography variant="h4" sx={{ fontWeight: 700, color: theme.palette.success.main }}>
                    {user?.connections || 0}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Connections
                  </Typography>
                </Box>
              </Stack>
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </Container>
  );
};

export default DashboardPage;
